import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ActionMenuItemType } from '@lyearn/molecules';
import useProvidersWithIntegrationDetails from '@/modules/corporate/modules/integrations/hooks/useProvidersWithIntegrationDetails';
import { IntegrationStatusEnum, ProviderAvailableOnEnum, ProviderType } from '@/types/schema';

import { AddTaskButtonProps, AddTaskMenuAction, InViewActionDialog } from '../../types';
import { getActionMenuItems } from '../../utils';

export default function useAddTasks(props: AddTaskButtonProps) {
  const { t } = useTranslation();
  const { entity } = props;

  const [inViewDialogState, setInViewDialogState] = useState<{
    inViewDialog: InViewActionDialog;
    provider?: ProviderType;
  }>();

  const { providers } = useProvidersWithIntegrationDetails({
    availableOn: [ProviderAvailableOnEnum.Okr],
  });
  const connectedProviders = useMemo(
    () =>
      providers?.filter(
        (provider) =>
          provider.integrationProvider?.integrationDetail?.status ===
          IntegrationStatusEnum.Connected,
      ),
    [providers],
  );

  const menuItems = useMemo(
    () => getActionMenuItems(t, connectedProviders, entity),
    [t, connectedProviders, entity],
  );
  const onMenuItemAction = useCallback((action: AddTaskMenuAction, item?: ActionMenuItemType) => {
    switch (action) {
      case AddTaskMenuAction.ImportFromJira:
        {
          setInViewDialogState({
            inViewDialog: InViewActionDialog.AddTaskFromJira,
            provider: (item as any).provider,
          });
        }
        break;
      case AddTaskMenuAction.AutoSyncFromJira:
        {
          setInViewDialogState({
            inViewDialog: InViewActionDialog.AutoSyncFromJira,
            provider: (item as any).provider,
          });
        }
        break;
      case AddTaskMenuAction.ImportFromExisting:
        {
          setInViewDialogState({
            inViewDialog: InViewActionDialog.AddTasks,
          });
        }
        break;
      default:
    }
  }, []);

  const onClose = useCallback(() => setInViewDialogState(undefined), []);

  return {
    state: {
      inViewDialogState,
      menuItems,
    },
    methods: {
      onClose,
      onMenuItemAction,
    },
  };
}
